<!-- WonderOpportunityMining.vue -->
<template>
  <div class="wonder-opportunity-page" :class="theme === 'dark' ? 'theme-dark' : 'theme-light'">
    <b-card no-body class="border-0 shadow-sm overflow-hidden mb-4 main-card">
      <b-card-body class="p-3">
        <!-- 头部筛选和工具栏 -->
        <div class="row align-items-center mb-4 flex-wrap">
          <div class="col-12 col-xl-8 d-flex align-items-center flex-wrap gap-3">
            <h4 class="font-weight-bold text-gradient mb-0 mr-4">机会挖掘</h4>
            
            <b-form-datepicker
              id="datepicker"
              v-model="selectedEndDate"
              :min="minDate"
              :max="maxDate"
              locale="zh"
              today-button
              reset-button
              close-button
              placeholder="选择日期"
              @input="onDateChange"
              class="mr-3 custom-datepicker"
              size="sm"
            ></b-form-datepicker>

            <div class="d-flex align-items-center flex-wrap gap-2 text-muted-custom">
              <span class="small">最近:
                <b-form-select v-model.number="dayn" :options="daynOptions" size="sm" class="ml-1 w-auto custom-select-sm" @change="onDaynChange"></b-form-select> 天
              </span>
              <span class="small ml-3">Top:
                <b-form-select v-model.number="topN" :options="topNOptions" size="sm" class="ml-1 w-auto custom-select-sm" @change="onSettingsChange"></b-form-select>
              </span>
              <span class="small ml-3">人气Top:
                <b-form-select v-model.number="popularTopN" :options="popularTopNOptions" size="sm" class="ml-1 w-auto custom-select-sm"></b-form-select>
              </span>
              <span class="small ml-3">周期:
                <b-form-radio-group
                  v-model="selectedCycle"
                  :options="cycleOptions"
                  buttons
                  button-variant="outline-primary"
                  size="sm"
                  class="ml-1 custom-radio-group"
                  @change="onSettingsChange"
                ></b-form-radio-group>
              </span>
            </div>
          </div>
          <div class="col-12 col-xl-4 text-xl-right mt-2 mt-xl-0">
            <b-button variant="light" size="sm" class="px-3 shadow-sm rounded-pill btn-refresh-custom" @click="get_trade_days" :disabled="loading">
              <b-icon icon="arrow-clockwise" :animation="loading ? 'spin' : ''"></b-icon> 刷新数据
            </b-button>
          </div>
        </div>

        <!-- 中间：多天题材轮动排名表格 -->
        <div v-if="loading && !Object.keys(ranksMap).length" class="text-center py-5">
          <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
          <div class="mt-3 text-muted">正在挖掘题材机会...</div>
        </div>
        
        <div v-else class="row no-gutters flex-nowrap opportunity-grid-container">
          <!-- 排名左侧边栏 -->
          <div class="col-auto px-1 rank-num-col">
            <div class="date-header-custom rank-header">排名</div>
            <div class="ranking-list">
              <div v-for="i in topN" :key="'rank-'+i" class="mb-1 text-center">
                <div class="ranking-num d-flex align-items-center justify-content-center" :class="{'rank-1': i===1, 'rank-2': i===2 || i===3}">
                  {{ i }}
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧交易日列 -->
          <div class="col overflow-hidden position-relative" style="min-width: 0;">
            <div class="row no-gutters flex-nowrap scroll-x-custom" ref="scrollGrid" @scroll="handleScroll('scrollGrid')">
              <div 
                v-for="day in days" 
                :key="day" 
                class="col px-2 day-column"
                style="min-width: 150px; max-width: 200px;"
              >
                <div class="date-header-custom">{{ formatDate(day) }}</div>
                <div class="topic-items-container">
                  <template v-if="ranksMap[day] && ranksMap[day].length">
                    <div 
                      v-for="item in ranksMap[day].slice(0, topN)" 
                      :key="item.topic_id"
                      class="mb-1"
                    >
                      <div 
                        class="topic-item-card transition-all"
                        :class="{'selected': selectedTopicId === item.topic_id}"
                        @click="selectTopic(item, day)"
                        :title="item.topic_content"
                      >
                        <div class="topic-name-row d-flex justify-content-between align-items-center">
                          <span class="name text-truncate">{{ item.topic_name }}</span>
                          <span class="rate" :class="item.sum_rate >= 0 ? 'text-up' : 'text-down'">
                            {{ item.sum_rate >= 0 ? '+' : '' }}{{ item.sum_rate }}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="text-center py-5 text-muted small">无数据</div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 下半部：个股人气 / 板块K线 切换面板 -->
        <div v-if="selectedTopicId" class="mt-3 border-top pt-3">
          <div class="row no-gutters flex-nowrap opportunity-grid-container">
            <!-- 左侧 模式切换边栏 (60px 宽度) -->
            <div class="col-auto px-1 rank-num-col d-flex flex-column align-items-center pt-2">
              <div 
                class="mode-switch-tab mb-2" 
                :class="{'active': subMode === 'popular'}"
                @click="subMode = 'popular'"
                title="个股人气"
              >
                <b-icon icon="people-fill" font-scale="1.2" class="mb-1"></b-icon>
                <span>人气</span>
              </div>
              <div 
                class="mode-switch-tab" 
                :class="{'active': subMode === 'kline'}"
                @click="subMode = 'kline'"
                title="指数K线"
              >
                <b-icon icon="graph-up" font-scale="1.2" class="mb-1"></b-icon>
                <span>K线</span>
              </div>
            </div>

            <!-- 右侧内容区域 -->
            <div class="col overflow-hidden position-relative" style="min-width: 0;">
              <!-- 人气个股列表 -->
              <div v-if="subMode === 'popular'" class="popular-stocks-container">
                <div class="row no-gutters flex-nowrap scroll-x-custom" ref="scrollPopular" @scroll="handleScroll('scrollPopular')">
                  <div 
                    v-for="(day, idx) in days" 
                    :key="'popular-col-'+day" 
                    class="col px-2 day-column"
                    style="min-width: 150px; max-width: 200px;"
                  >
                    <!-- Date Header -->
                    <div class="date-header-custom">{{ formatDate(day) }}</div>
                    
                    <TopicStockPopularTable
                      :topic_id="selectedTopicId"
                      :date1="day"
                      :top-n="popularTopN"
                      :is-latest="idx === 0"
                      :latest-top-three="latestTopThree"
                      :clicked-stock-name="clickedStockName"
                      :theme="theme"
                      @latest-top-three="handleLatestTopThree"
                      @stock-clicked="handleStockClicked"
                      @show_sidebar="show_sidebar_from_comp"
                    />
                  </div>
                </div>
              </div>

              <!-- K线面板 -->
              <div v-else-if="subMode === 'kline'" class="kline-container rounded border p-3 bg-card-custom shadow-sm position-relative">
                <div v-if="loadingKline" class="text-center py-5">
                  <b-spinner variant="primary"></b-spinner>
                  <div class="mt-2 text-muted small">加载题材K线数据...</div>
                </div>
                
                <div v-if="requireSubscription" class="locked-overlay d-flex flex-column align-items-center justify-content-center p-4 text-center">
                  <div class="lock-circle mb-3">
                    <b-icon icon="lock-fill" font-scale="1.8"></b-icon>
                  </div>
                  <h5 class="font-weight-bold mb-2">开通题材表格 VIP</h5>
                  <p class="text-muted small mb-3">解锁题材指数K线和多维指标回测分析。</p>
                  <b-button size="sm" variant="warning" class="rounded-pill px-4 font-weight-bold" @click="$router.push('/subscription')">
                    立即订阅
                  </b-button>
                </div>

                <div :class="{'blur-content': requireSubscription}">
                  <div class="d-flex justify-content-between align-items-center mb-2 px-2 flex-wrap">
                    <span class="small font-weight-bold text-muted-custom">指数多维K线走势图</span>
                    <b-button variant="outline-secondary" size="sm" class="rounded-pill" :disabled="syncingKline" @click="syncKlineData">
                      <b-icon icon="arrow-repeat" :animation="syncingKline ? 'spin' : ''"></b-icon> 同步计算K线
                    </b-button>
                  </div>
                  <v-chart 
                    :key="selectedTopicId + ':' + echartsTheme" 
                    :style="chartStyle()" 
                    :options="klineOption" 
                    :theme="echartsTheme" 
                    :notMerge="true" 
                    :autoresize="true" 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="text-center py-5 text-muted-custom small">
          请从上方列表中点击选择题材以查看人气股和指数K线图
        </div>

      </b-card-body>
    </b-card>

    <StockInfoSidebar :symbol_code="sidebarSymbolCode" :symbol_name="sidebarSymbolName" :date_mark="sidebarDateMark" />
  </div>
</template>

<script>
import moment from 'moment'
import ECharts from 'vue-echarts'
import { request_zzquant } from 'network/request.js'
import StockInfoSidebar from '@/components/stock/StockInfoSidebar'
import TopicStockPopularTable from '@/components/plate/TopicStockPopularTable'

export default {
  name: 'WonderOpportunityMining',
  components: {
    'v-chart': ECharts,
    StockInfoSidebar,
    TopicStockPopularTable
  },
  data() {
    return {
      theme: 'light',
      loading: false,
      loadingKline: false,
      syncingKline: false,
      requireSubscription: false,
      minDate: moment().subtract(3, 'year').format('YYYY-MM-DD'),
      maxDate: moment().format('YYYY-MM-DD'),
      selectedEndDate: (() => {
        const now = moment()
        const currentTime = now.format('HH:mm')
        if (currentTime < '09:31') {
          return now.format('YYYY-MM-DD')
        } else {
          return now.add(1, 'days').format('YYYY-MM-DD')
        }
      })(),
      dayn: 10,
      daynOptions: [6, 8, 10, 12, 15],
      topN: 10,
      topNOptions: [5, 10, 12, 20],
      popularTopN: 12,
      popularTopNOptions: [5, 10, 12, 15, 20],
      selectedCycle: 1,
      cycleOptions: [
        { text: '1日', value: 1 },
        { text: '3日', value: 3 },
        { text: '5日', value: 5 },
        { text: '10日', value: 10 }
      ],
      days: [],
      allDays: [],
      ranksMap: {},
      
      selectedTopicId: null,
      selectedTopicName: '',
      selectedTopicDate: '',
      subMode: 'popular',
      latestTopThree: [],
      clickedStockName: null,
      popularDate: '',
      klineData: [],
      klineOption: {},
      
      sidebarSymbolCode: '',
      sidebarSymbolName: '',
      sidebarDateMark: ''
    }
  },
  computed: {
    echartsTheme() {
      return this.theme === 'dark' ? 'site-dark' : null
    }
  },
  created() {
    if (!this.checkAdmin()) {
      this.$router.replace('/wonder/yjj/ml')
    }
  },
  watch: {
    subMode(newMode) {
      if (newMode === 'kline') {
        this.fetchKlineData()
      }
    },
    selectedTopicId(newVal) {
      if (newVal) {
        if (this.subMode === 'kline') {
          this.fetchKlineData()
        }
      }
    },
    selectedTopicDate(newVal) {
      if (newVal && this.klineData.length > 0) {
        this.generateKlineOption()
      }
    }
  },
  mounted() {
    this.get_trade_days()
    
    try {
      const t = localStorage.getItem('site.theme')
      if (t) {
        this.theme = t
      }
    } catch (e) {
      console.warn('Theme read failed', e)
    }
    
    this.onThemeChanged = (e) => {
      const t = e && e.detail && e.detail.theme ? e.detail.theme : (localStorage.getItem('site.theme') || 'light')
      this.theme = t
      if (this.subMode === 'kline' && this.klineData.length > 0) {
        this.generateKlineOption()
      }
    }
    window.addEventListener('site-theme-changed', this.onThemeChanged)
  },
  beforeDestroy() {
    window.removeEventListener('site-theme-changed', this.onThemeChanged)
  },
  methods: {
    checkAdmin() {
      try {
        const raw = localStorage.getItem('user_info')
        if (raw) {
          const info = JSON.parse(raw)
          const r = info && info.roles
          const roles = Array.isArray(r) ? r : String(r || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
          if (roles.includes('admin')) return true
        }
        const token = localStorage.getItem('token')
        if (token) {
          const parts = token.split('.')
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]))
            const r = payload && payload.roles
            const roles = Array.isArray(r) ? r : String(r || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
            if (roles.includes('admin')) return true
          }
        }
        return false
      } catch (e) {
        return false
      }
    },
    chartStyle() {
      return {
        width: '100%',
        height: '400px'
      }
    },
    formatDate(day) {
      if (!day) return ''
      try {
        const d = new Date(day)
        const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        return `${mm}/${dd} ${weekdays[d.getDay()]}`
      } catch (e) {
        return day
      }
    },
    formatYi(val) {
      if (val === undefined || val === null || val === '') return '-'
      const n = parseFloat(val)
      if (isNaN(n)) return String(val)
      const yi = n / 100000000
      return yi.toFixed(2) + '亿'
    },
    onDateChange() {
      this.get_trade_days()
    },
    onDaynChange() {
      this.get_trade_days()
    },
    onSettingsChange() {
      this.loadAllRanks()
    },
    get_trade_days() {
      const today = this.selectedEndDate
      request_zzquant({
        url: '/market/trade/days?day_end=' + today + '&days=' + (this.dayn + 3)
      })
      .then(res => {
        const raw = res['data']['data'] || []
        this.allDays = raw.slice().sort((a, b) => new Date(b) - new Date(a))
        this.days = this.allDays.slice(0, this.dayn)
        this.loadAllRanks()
      })
      .catch(err => {
        console.error('Failed to load trade days', err)
      })
    },
    async loadAllRanks() {
      this.loading = true
      this.ranksMap = {}
      const promises = this.days.map(async (day) => {
        try {
          const res = await request_zzquant({
            url: '/v3/topic/tables/rank',
            method: 'GET',
            params: {
              date2: day,
              n_days: this.selectedCycle,
              limit: this.topN
            }
          })
          if (res.data && res.data.code === 20000) {
            this.$set(this.ranksMap, day, res.data.data || [])
          } else {
            this.$set(this.ranksMap, day, [])
          }
        } catch (e) {
          console.error('Failed to load ranks for', day, e)
          this.$set(this.ranksMap, day, [])
        }
      })
      await Promise.all(promises)
      this.loading = false
      
      if (this.days.length > 0) {
        const latestRanks = this.ranksMap[this.days[0]] || []
        if (latestRanks.length > 0) {
          const found = latestRanks.find(r => r.topic_id === this.selectedTopicId)
          if (!found) {
            this.selectTopic(latestRanks[0], this.days[0])
          }
        }
      }
    },
    selectTopic(item, day) {
      this.selectedTopicId = item.topic_id
      this.selectedTopicName = item.topic_name
      this.selectedTopicDate = day || ''
    },
    handleLatestTopThree(topThree) {
      this.latestTopThree = topThree
    },
    handleStockClicked(stockName) {
      if (this.clickedStockName === stockName) {
        this.clickedStockName = null
      } else {
        this.clickedStockName = stockName
      }
    },
    show_sidebar_from_comp(stock_info) {
      this.sidebarSymbolCode = stock_info.stock_code
      this.sidebarSymbolName = stock_info.stock_name
      this.sidebarDateMark = stock_info.date1
    },
    fetchKlineData() {
      if (!this.selectedTopicId) return
      this.loadingKline = true
      this.requireSubscription = false
      
      request_zzquant({
        url: `/v3/topic/table/${this.selectedTopicId}/kline`,
        method: 'GET'
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          this.klineData = res.data.data || []
          this.generateKlineOption()
        }
      })
      .catch(err => {
        const status = err.status || (err.response && err.response.status)
        if (status === 403) {
          this.requireSubscription = true
          this.klineData = []
        } else {
          console.error('Failed to load kline data', err)
        }
      })
      .finally(() => {
        this.loadingKline = false
      })
    },
    syncKlineData() {
      if (!this.selectedTopicId) return
      this.syncingKline = true
      
      request_zzquant({
        url: `/v3/topic/table/${this.selectedTopicId}/kline/sync`,
        method: 'POST'
      })
      .then(() => {
        this.$bvToast.toast('K线计算任务已启动，请稍后', { variant: 'info', solid: true })
        setTimeout(() => this.fetchKlineData(), 3000)
      })
      .catch(e => {
        console.error('Failed to sync kline data', e)
      })
      .finally(() => {
        this.syncingKline = false
      })
    },
    generateKlineOption() {
      if (!this.klineData || this.klineData.length === 0) return
      
      const dates = this.klineData.map(d => d.trade_date)
      const values = this.klineData.map(d => [d.open, d.close, d.low, d.high, d.prev_close])
      const volumes = this.klineData.map(d => d.amount)
      
      const upColor = this.theme === 'dark' ? '#ef4444' : '#ec0000'
      const downColor = this.theme === 'dark' ? '#10b981' : '#00da3c'
      const textColor = this.theme === 'dark' ? '#94a3b8' : '#64748b'
      
      let markLineOpt = {}
      if (this.selectedTopicDate) {
        const idx = dates.indexOf(this.selectedTopicDate)
        if (idx !== -1) {
          markLineOpt = {
            silent: true,
            symbol: 'none',
            label: {
              show: true,
              position: 'end',
              formatter: this.formatDate(this.selectedTopicDate),
              fontSize: 10,
              color: '#3b82f6'
            },
            data: [
              {
                name: this.formatDate(this.selectedTopicDate),
                xAxis: this.selectedTopicDate,
                lineStyle: {
                  width: 2,
                  type: 'dashed',
                  color: '#3b82f6',
                  opacity: 0.8
                }
              }
            ]
          }
        }
      }
      
      this.klineOption = {
        animation: false,
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' },
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: '#333',
          textStyle: { color: '#eee', fontSize: 12 },
          formatter: (params) => {
            let res = params[0].name + '<br/>'
            params.forEach(p => {
              if (p.seriesName === '题材K') {
                const d = p.value
                const close = d[2]
                const pre_close = d[5]
                const change = (((close - pre_close) / pre_close) * 100).toFixed(2)
                res += `${p.marker} 指数: <span style="color:${close >= d[1] ? upColor : downColor}">${close} (${change}%)</span><br/>`
                res += `开盘: ${d[1]} 最高: ${d[3]} 最低: ${d[4]}<br/>`
              } else if (p.seriesName === '均成交额') {
                res += `${p.marker} 均成交额: ${(p.value / 100000000).toFixed(2)} 亿<br/>`
              }
            })
            return res
          }
        },
        grid: [
          { left: '60', right: '40', top: '8%', height: '55%' },
          { left: '60', right: '40', top: '70%', height: '18%' }
        ],
        xAxis: [
          {
            type: 'category',
            data: dates,
            boundaryGap: true,
            axisLine: { onZero: false, lineStyle: { color: textColor } },
            splitLine: { show: false },
            axisLabel: { color: textColor, fontSize: 10 }
          },
          {
            type: 'category',
            gridIndex: 1,
            data: dates,
            boundaryGap: true,
            axisLine: { onZero: false, lineStyle: { color: textColor } },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false }
          }
        ],
        yAxis: [
          {
            scale: true,
            splitLine: { show: true, lineStyle: { type: 'dashed', opacity: 0.1 } },
            axisLabel: { color: textColor, fontSize: 10 }
          },
          {
            scale: true,
            gridIndex: 1,
            splitNumber: 2,
            axisLabel: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false }
          }
        ],
        dataZoom: [
          {
            show: true,
            type: 'slider',
            xAxisIndex: [0, 1],
            top: '92%',
            start: 40,
            end: 100,
            textStyle: { color: textColor }
          }
        ],
        series: [
          {
            name: '题材K',
            type: 'candlestick',
            data: values,
            itemStyle: {
              color: upColor,
              color0: downColor,
              borderColor: upColor,
              borderColor0: downColor
            },
            markLine: Object.keys(markLineOpt).length > 0 ? markLineOpt : undefined
          },
          {
            name: '均成交额',
            type: 'bar',
            xAxisIndex: 1,
            yAxisIndex: 1,
            data: volumes,
            itemStyle: {
              color: (params) => {
                const val = values[params.dataIndex]
                if (val) {
                  return val[1] >= val[0] ? upColor : downColor
                }
                return upColor
              }
            }
          }
        ]
      }
    },
    showStockDetail(stockCode, stockName, day) {
      this.sidebarSymbolCode = stockCode
      this.sidebarSymbolName = stockName
      this.sidebarDateMark = day || this.popularDate
    },
    handleScroll(active) {
      const activeRef = this.$refs[active]
      if (!activeRef) return
      const scrollLeft = activeRef.scrollLeft
      const refs = ['scrollGrid', 'scrollPopular']
      refs.forEach(r => {
        if (r !== active && this.$refs[r]) {
          this.$refs[r].scrollLeft = scrollLeft
        }
      })
    }
  }
}
</script>

<style scoped>
.wonder-opportunity-page {
  --bg-main-custom: #111827;
  --bg-card-custom: #1f2937;
  --bg-card-hover: #374151;
  --bg-header-custom: #374151;
  --border-color-custom: #374151;
  --text-main-custom: #f3f4f6;
  --text-muted-custom: #9ca3af;
  --primary-custom: #3b82f6;
}

.theme-light {
  --bg-main-custom: #f9fafb;
  --bg-card-custom: #ffffff;
  --bg-card-hover: #f3f4f6;
  --bg-header-custom: #f3f4f6;
  --border-color-custom: #e5e7eb;
  --text-main-custom: #1f2937;
  --text-muted-custom: #6b7280;
  --primary-custom: #2563eb;
}

.main-card {
  background-color: var(--bg-card-custom);
  color: var(--text-main-custom);
  border: 1px solid var(--border-color-custom);
}

.text-gradient {
  background: linear-gradient(135deg, var(--primary-custom), #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.text-up {
  color: #ef4444 !important;
}
.text-down {
  color: #10b981 !important;
}

.text-muted-custom {
  color: var(--text-muted-custom);
}

.custom-datepicker {
  max-width: 200px;
  background-color: var(--bg-card-custom);
  color: var(--text-main-custom);
  border: 1px solid var(--border-color-custom);
}

.custom-select-sm {
  background-color: var(--bg-card-custom);
  color: var(--text-main-custom);
  border: 1px solid var(--border-color-custom);
}

.btn-refresh-custom {
  background-color: var(--bg-header-custom);
  color: var(--text-main-custom);
  border: 1px solid var(--border-color-custom);
}
.btn-refresh-custom:hover {
  background-color: var(--bg-card-hover);
}

.opportunity-grid-container {
  overflow-x: auto;
  padding-bottom: 12px;
}

.rank-num-col {
  width: 60px;
}

.date-header-custom {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-main-custom);
  background-color: var(--bg-header-custom);
  border-bottom: 1px solid var(--border-color-custom);
  border-radius: 6px;
  padding: 6px 0;
  margin-bottom: 8px;
  text-align: center;
  line-height: 1.2rem;
  min-height: 32px;
  white-space: nowrap;
}

.rank-header {
  font-weight: bold;
}

.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ranking-num {
  border: 1px solid var(--border-color-custom);
  color: var(--text-main-custom);
  background-color: var(--bg-card-custom);
  border-radius: 6px;
  padding: 4px;
  width: 100%;
  min-height: 38px;
  font-size: 13px;
  font-weight: bold;
}

.rank-1 {
  background-color: #dc3545 !important;
  color: #ffffff !important;
  border-color: #dc3545 !important;
}

.rank-2 {
  background-color: #ffc107 !important;
  color: #1f2937 !important;
  border-color: #ffc107 !important;
}

.scroll-x-custom {
  overflow-x: auto;
  scroll-behavior: smooth;
}

.day-column {
  border-right: 1px solid var(--border-color-custom);
}
.day-column:last-child {
  border-right: none;
}

.topic-items-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.topic-item-card {
  border: 1px solid var(--border-color-custom);
  background-color: var(--bg-card-custom);
  border-radius: 6px;
  padding: 8px 10px;
  min-height: 38px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.topic-item-card:hover {
  background-color: var(--bg-card-hover);
  transform: translateY(-1px);
}
.topic-item-card.selected {
  border-color: var(--primary-custom);
  background-color: rgba(59, 130, 246, 0.1);
  box-shadow: 0 0 0 1px var(--primary-custom);
}

.topic-name-row {
  width: 100%;
  font-size: 12px;
  font-weight: 500;
}
.topic-name-row .name {
  max-width: 65%;
}
.topic-name-row .rate {
  font-weight: bold;
}

/* Mode Switch Tab styles in the left sidebar */
.mode-switch-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 48px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-muted-custom);
  border: 1px solid transparent;
  transition: all 0.2s ease;
  font-size: 10px;
  font-weight: bold;
  line-height: 1.2;
}

.mode-switch-tab:hover {
  background-color: var(--bg-card-hover);
  color: var(--text-main-custom);
}

.mode-switch-tab.active {
  background-color: var(--primary-custom);
  color: #ffffff !important;
  border-color: var(--primary-custom);
}

.mode-switch-tab span {
  margin-top: 3px;
  font-size: 10px;
}

.bg-card-custom {
  background-color: var(--bg-card-custom) !important;
  border-color: var(--border-color-custom) !important;
}

.custom-table {
  background-color: var(--bg-card-custom);
  color: var(--text-main-custom);
}
.custom-table th {
  border-bottom: 2px solid var(--border-color-custom);
  color: var(--text-muted-custom);
  font-size: 0.8rem;
  text-transform: uppercase;
}
.custom-table td {
  border-top: 1px solid var(--border-color-custom);
  font-size: 0.85rem;
  vertical-align: middle;
}

.code-font {
  font-family: 'Courier New', Courier, monospace;
}

.rank-badge {
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 4px;
}


.locked-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(31, 41, 55, 0.85);
  backdrop-filter: blur(4px);
  z-index: 10;
  border-radius: 6px;
}

.lock-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(255, 193, 7, 0.15);
  color: #ffc107;
  display: flex;
  align-items: center;
  justify-content: center;
}

.blur-content {
  filter: blur(3px);
  pointer-events: none;
}

.transition-all {
  transition: all 0.25s ease;
}
</style>
